/**
 * useVoyagePlanItemState.js — Sub-ticket C
 *
 * Per-voyage-plan-item state mutations.
 *
 * Voyage-plan items live at voyage.voyagePlanItems[itemId] (initialised in
 * Sub-ticket B at Let Go promotion by voyagePlanResolution.js). This hook owns
 * ALL mutations to that map. It is a SEPARATE FILE — synthesis A4 LOCKED — and
 * does NOT live inside useVoyages.js (3000+ lines, 2 TDZ regressions in
 * FT-270; see feedback_runtime_smoke_test_before_done.md). It is composed
 * inside useVoyages and re-exported on its surface, so consumers never need to
 * touch setVoyages directly.
 *
 * Read sites of voyage.voyagePlanItems[itemId] (VoyagePlanUnderwayCard) MUST
 * tolerate `undefined` for backward compatibility — voyages predating
 * Sub-ticket B carry none of these fields.
 *
 * Guard policy mirrors useVoyages.js:1498-1543 (updateArrivalCheck) precedent:
 *   - RBAC: canDo(can, 'voyages', 'update')
 *   - State machine: voyage status MUST be UNDERWAY or ARRIVAL
 *   - Atomic write: single setVoyages(prev => ...) functional updater
 *
 * Item state enum (synthesis §3.5):
 *   waypoint:      'pending' → 'completed' | 'skipped'                (skip 'in_progress')
 *   operation_ref: 'pending' → 'in_progress' → 'completed' | 'skipped'
 *
 * @example
 *   const { updatePlanItem, skipPlanItem } = useVoyagePlanItemState({
 *     voyages, setVoyages, currentUser, can,
 *   });
 *   await updatePlanItem(voyageId, planItemId, { status: 'completed', linkedLogEntryId, resolvedAt });
 *   await skipPlanItem(voyageId, planItemId, 'Weather delay — skipped waypoint');
 */

import { useCallback } from 'react';
import { VOYAGE_STATUS } from '../../../config/operationsConfig';

// RBAC multi-signature shim — mirrors the helper inlined at
// useVoyages.js:51-64 + useMainLogbook.js:44 verbatim. Co-located so the hook
// has zero dependency on useVoyages.js (TDZ defence per A4).
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * @param {Object}   deps
 * @param {Array}    deps.voyages       — current voyages list (currently unused
 *                                        in the hot path — the functional updater
 *                                        reads prev — but kept in the signature
 *                                        for future read-only helpers).
 * @param {Function} deps.setVoyages    — useStorageState setter; MUST accept an
 *                                        updater function (prev) => next.
 * @param {Object}   deps.currentUser   — for resolvedBy / resolvedByName attribution.
 * @param {Function} deps.can           — permission resolver from usePermissions.
 * @returns {{
 *   updatePlanItem: (voyageId: string, planItemId: string, patch: Object) => Promise<boolean>,
 *   skipPlanItem:   (voyageId: string, planItemId: string, skipReason: string) => Promise<boolean>,
 * }}
 */
export function useVoyagePlanItemState({ voyages, setVoyages, currentUser, can }) {
  /**
   * Atomic per-item mutation.
   *
   * Mirrors updateArrivalCheck at useVoyages.js:1498-1543 — the functional
   * setVoyages(prev => ...) updater is the critical correctness primitive.
   * Two rapid taps (Master taps two waypoints in 200ms) would otherwise
   * clobber each other on a stale closure.
   *
   * Returns true on a successful write; false on RBAC denial, voyage-not-found,
   * or wrong-status (no-op). Never throws.
   *
   * @param {string} voyageId
   * @param {string} planItemId — the snapshot item id (NOT the operationId — see
   *                              CODING-BRIEF-C §2.3 planItemId correlation pattern).
   * @param {Object} patch — partial item-state object; merged onto the existing
   *                         item state via { ...prevItem, ...patch }.
   * @returns {Promise<boolean>}
   */
  const updatePlanItem = useCallback(async (voyageId, planItemId, patch) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyagePlanItemState] updatePlanItem blocked by RBAC');
      return false;
    }
    if (!voyageId || !planItemId) {
      console.warn('[useVoyagePlanItemState] updatePlanItem missing voyageId/planItemId');
      return false;
    }

    let success = false;
    await setVoyages(prevVoyages => {
      const voyage = (prevVoyages || []).find(v => v.id === voyageId);
      if (!voyage) {
        console.warn('[useVoyagePlanItemState] voyage not found:', voyageId);
        return prevVoyages;
      }
      // Mirror updateArrivalCheck precedent — UNDERWAY or ARRIVAL only.
      // DRAFT / PRE_DEPARTURE / COMPLETED / CANCELLED / ABORTED all bail.
      if (voyage.status !== VOYAGE_STATUS.UNDERWAY && voyage.status !== VOYAGE_STATUS.ARRIVAL) {
        console.warn(
          '[useVoyagePlanItemState] plan-item update blocked — voyage not UNDERWAY/ARRIVAL (status:',
          voyage.status, ')'
        );
        return prevVoyages;
      }
      // Defensive: a pre-B voyage may not have voyagePlanItems. Treat as empty.
      // Should never happen post-B (Let Go promotion always initialises) but
      // cheap to guard.
      const prevItems = voyage.voyagePlanItems || {};
      const prevItem = prevItems[planItemId] || { status: 'pending' };

      success = true;
      return prevVoyages.map(v =>
        v.id === voyageId
          ? {
              ...v,
              voyagePlanItems: {
                ...prevItems,
                [planItemId]: { ...prevItem, ...patch },
              },
              updatedAt: new Date().toISOString(),
            }
          : v
      );
    });

    return success;
  }, [can, setVoyages]);

  /**
   * Skip sugar — single canonical skip patch with attribution.
   *
   * Audit footnote (CODING-BRIEF-C §1.2): plan-item state mutations are NOT
   * audited as a discrete `voyage.plan_item_skipped` row in C. Sub-ticket D
   * adds that audit action. The underlying voyage_position_logged /
   * voyage_operation_completed audit rows still fire as today and now carry
   * planItemId in moduleSpecificData (see useVoyages.js completeOperationV2
   * + logPositionV2 + bulkSkipAndComplete edits in §4).
   *
   * @param {string} voyageId
   * @param {string} planItemId
   * @param {string} skipReason — free-text; defaults to 'No reason given' if absent.
   * @returns {Promise<boolean>}
   */
  const skipPlanItem = useCallback((voyageId, planItemId, skipReason) => {
    return updatePlanItem(voyageId, planItemId, {
      status: 'skipped',
      skipReason: typeof skipReason === 'string' && skipReason.trim() ? skipReason.trim() : 'No reason given',
      resolvedAt: new Date().toISOString(),
      resolvedBy: currentUser?.id || null,
      resolvedByName: currentUser?.name || 'Unknown',
    });
  }, [updatePlanItem, currentUser]);

  return {
    updatePlanItem,
    skipPlanItem,
  };
}

export default useVoyagePlanItemState;
